import { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { Link } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { BaseRedirectLinkContainer } from "@circle-react/components/OnboardingComponents/BaseRedirectLinkContainer";
import { WebviewAuthenticationLayout } from "@circle-react/components/OnboardingPage/WebviewAuthenticationLayout";
import { PageTitle } from "@circle-react/components/OnboardingPage/layouts/PageTitle";
import { useOnboardingWhiteLabel } from "../hooks/useOnboardingWhiteLabel";
import { ResetPasswordForm } from "./ResetPasswordForm";
import { ResetPasswordLayout } from "./ResetPasswordLayout";

interface CurrentCommunityContextType {
  canCurrentCommunitySupportCustomLogin: boolean;
  currentCommunity: any;
  userEmail: string;
}

interface ResetPasswordPageProps {
  otpSignInResponse: {
    redirectUrl: string;
    currentCommunityName: string;
    otherCommunityNames: string[] | null;
  } | null;
}

function toCurrentCommunityContext(obj: any): CurrentCommunityContextType {
  return obj;
}

export const ResetPasswordPage = ({
  otpSignInResponse,
}: ResetPasswordPageProps) => {
  const pageTitle = t("sign_in");
  const [shouldShowForm, setShouldShowForm] = useState<boolean>(false);
  const result = toCurrentCommunityContext(useCurrentCommunityContext());
  const { currentCommunity, userEmail, canCurrentCommunitySupportCustomLogin } =
    result;

  const { shouldShowPoweredByCircle } =
    useOnboardingWhiteLabel(currentCommunity);

  const currentHash = window.location.hash;
  const title = t("change_your_password");
  const isInReactNativeAuthenticatedWebview: boolean =
    !!window.webview?.isComingFromReactNativeAuthenticatedWebview ||
    window.webview.isInsideMobileWebview;
  const isInReactNativeWebview =
    !!window.webview?.isComingFromReactNativeWebview;

  useEffect(() => {
    setShouldShowForm(currentHash === "#email");
  }, [currentHash]);

  // set a placeholder title for the page when authentication using an authenticated webview on mobile
  if (isInReactNativeAuthenticatedWebview) {
    return <WebviewAuthenticationLayout />;
  }

  if (!shouldShowForm) return null;

  return (
    <ResetPasswordLayout
      community={currentCommunity}
      title={title}
      userEmail={userEmail}
      communityName={otpSignInResponse?.currentCommunityName}
      otherCommunityNames={otpSignInResponse?.otherCommunityNames}
      canCurrentCommunitySupportCustomLogin={
        canCurrentCommunitySupportCustomLogin
      }
      isNarrow={false}
      withCircleBranding={isEmpty(currentCommunity)}
      shouldShowPoweredByCircle={shouldShowPoweredByCircle}
    >
      <PageTitle title={pageTitle} />
      <div>
        <ResetPasswordForm userEmail={userEmail} />
      </div>
      {!isInReactNativeWebview && (
        <div className="mt-8 flex flex-col items-center justify-center">
          <div className="mb-6" id="back-link-container">
            <BaseRedirectLinkContainer>
              <Link
                to="#"
                onClick={() =>
                  (window.location.href = otpSignInResponse?.redirectUrl || "/")
                }
              >
                {t("skip")}
              </Link>
            </BaseRedirectLinkContainer>
          </div>
        </div>
      )}
    </ResetPasswordLayout>
  );
};
